import { useMemo, useState } from "react";
import type { InsightCallout } from "../types";

type UseInsightPanelStateArgs = {
  insights: InsightCallout[];
  onInsightsChange: (insights: InsightCallout[]) => void;
};

export function useInsightPanelState({
  insights,
  onInsightsChange,
}: UseInsightPanelStateArgs) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const acceptedInsights = useMemo(
    () => insights.filter((insight) => insight.is_accepted),
    [insights],
  );
  const removedInsights = useMemo(
    () => insights.filter((insight) => !insight.is_accepted),
    [insights],
  );

  const beginEdit = (insight: InsightCallout) => {
    setEditingId(insight.id);
    setEditTitle(insight.title);
    setEditDescription(insight.description);
  };

  const saveEdit = (insightId: string) => {
    const updatedInsights = insights.map((insight) =>
      insight.id === insightId
        ? {
            ...insight,
            title: editTitle,
            description: editDescription,
            is_edited: true,
          }
        : insight,
    );
    onInsightsChange(updatedInsights);
    setEditingId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setEditDescription("");
  };

  const updateInsightAcceptance = (
    insightId: string,
    isAccepted: boolean,
  ) => {
    const updatedInsights = insights.map((insight) =>
      insight.id === insightId ? { ...insight, is_accepted: isAccepted } : insight,
    );
    onInsightsChange(updatedInsights);
  };

  return {
    acceptedInsights,
    beginEdit,
    cancelEdit,
    editDescription,
    editTitle,
    editingId,
    removedInsights,
    saveEdit,
    setEditDescription,
    setEditTitle,
    updateInsightAcceptance,
  };
}
